<script setup lang="ts">
import {computed, reactive, ref, watchEffect} from "vue";
import ContactSelect from "../Contacts/ContactSelect.vue";
import AddContactDrawer from "../Contacts/AddContactDrawer.vue";
import {Avatar, Button, InputText, Message} from "@/Components/Library";
import {syncGroupMembers} from "@/Services/group-members";
import {getContacts as getContactsFromApi} from "@/Services/contacts";
import {PageLoading, PageTitle} from "@/Components/Global";
import LaunchHeader from "../Questions/Launch/LaunchHeader.vue";
import {useRouter} from "vue-router";
import {watchDebounced} from "@vueuse/core";
import {useMainStore} from "@/Stores/store";
import {resendInvitation} from "@/Services/invitations";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import useEcho from "@/Composables/echo";

const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const currentRoute = router.currentRoute;
const $store = useMainStore();

const cancel = () => {
    router.push({name: "questions.index"});
};

const props = defineProps<{
    group: any;
}>();
const emit = defineEmits<{
    memberAdded: [string, string];
    invitationWithdrawn: [string];
}>();

const state = reactive({
    show: {
        addContactDialog: false,
        pendingInvitations: false,
    },
    loading: {
        contacts: true,
    },
    search: "",
});

const contacts = ref([]);
const totalContacts = ref(0);
// available contacts are contacts who are not in the invitations. Check invitation invitee_id against contact id
const availableContacts = computed(() => {
    return contacts.value.filter(
        (contact) => !props.group.invitations.some((invitation) => invitation.invitee?.id === contact.id),
    );
});
const selectedMembers = ref(props.group?.members.map((member) => member.id) ?? []);
const submit = () => {
    if (!selectedMembers.value.length) {
        confirm.require({
            header: "No members selected",
            message: "You have not selected any members, are you sure you want to continue?",
            acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
            rejectClass: "text-red-600 px-2 py-1 rounded-lg hover:brightness-110",
            accept: async () => {
                await syncMembers();
            },
        });
        return;
    }

    syncMembers();
};
const syncMembers = async () => {
    const {data} = await syncGroupMembers(props.group.id, selectedMembers.value);
    console.log(data);
    // return;
    // group.value.members = data;
    // state.show.manageGroup = false;
    // console.log(data);
    if (currentRoute.value.query.launch) {
        router.push({
            name: "questions.launch",
            query: {group: props.group.id},
        });
        return;
    }
    $store.group.id = null;
    router.push({name: "feed", params: {group: props.group.id}});
};
// getContacts({active: true}).then(({data}) => (contacts.value = data));
const getContacts = async () => {
    try {
        state.loading.contacts = true;
        const {data, total} = await getContactsFromApi({
            active: true,
            filter: {
                search: state.search,
            },
        });
        contacts.value = data;
        totalContacts.value = total;
    } catch (e) {
        console.log(e);
    } finally {
        state.loading.contacts = false;
    }
};
getContacts();

const handleResendInvitation = (invitation) => {
    confirm.require({
        header: `Resend invitation`,
        message: `Are you sure you want to resend this invitation?`,
        acceptLabel: "Yes",
        rejectLabel: "No",
        acceptClass: "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
        rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
        accept: async () => {
            await resendInvitation(invitation.id);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "Invitation has been resent.",
                life: 3000,
            });
        },
    });
};

const handleWithdrawInvitation = (invitation) => {
    confirm.require({
        header: `Withdraw invitation`,
        message: `Are you sure you want to withdraw this invitation?`,
        acceptLabel: "Yes",
        rejectLabel: "No",
        acceptClass: "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
        rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
        accept: () => emit("invitationWithdrawn", invitation.id),
    });
};

useEcho([
    {
        event: "Groups\\GroupMemberAdded",
        callback: (e: {group: string; user: string; invitation: string}) => {
            if (e.group !== props.group.id) {
                return;
            }

            emit("memberAdded", e.user, e.invitation);
        },
    },
]);

watchEffect(() => {
    if (props.group) {
        const selectedMembers = props.group?.members.map((member) => member.id) ?? [];
        state.show.pendingInvitations = !selectedMembers.length;
        selectedMembers.value = selectedMembers;
    }
});
watchDebounced(() => state.search, getContacts, {debounce: 1000});
</script>
<template>
    <div class="m-container mx-auto max-w-7xl">
        <div v-if="currentRoute.query.launch" class="mb-2">
            <LaunchHeader @cancel="cancel">
                <div class="pb-3">
                    <h3 class="font-medium">Create a group</h3>
                    <p class="text-sm">this will be the audience for your post</p>
                </div>
            </LaunchHeader>
        </div>
        <PageTitle class="mb-2" title="Manage Group Members" :route="{name: 'feed', params: {group: group.id}}" />
        <div v-if="group">
            <div class="mb-2 flex items-center gap-2">
                <Avatar
                    class="h-8 w-8 text-xs"
                    :class="[`bg-${group.color?.class}`, group.color?.class === 'white' ? 'text-black' : 'text-white']"
                    :label="group.icon ? undefined : group.initials"
                    :image="group.icon?.url"
                />
                <h2 class="text--lg font-bold sm:text-2xl">
                    {{ group.name }}
                </h2>
                <div class="ml-auto">
                    <RouterLink :to="{name: 'groups.edit', params: {group: group.id}}">
                        <Button
                            class="h-10 w-10 border bg-stone-50 !p-0 shadow-sm dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300"
                            icon="lucide:edit"
                        />
                    </RouterLink>
                </div>
            </div>
            <!--    <TagList :tags="question.tags" class="mb-3" />-->
            <div class="mb-8 flex flex-col items-start">
                <!--                <div class="flex items-center gap-1 text-stone-500">-->
                <!--                    <Icon icon="mdi:text-box-outline" width="24" />-->
                <!--                    <h4 class="text-sm font-bold">Description</h4>-->
                <!--                </div>-->
                <p>{{ group.description }}</p>
            </div>
            <template v-if="contacts.length">
                <div class="mb-6 flex flex-col items-start">
                    <h3 class="mb-1 font-bold">Search</h3>
                    <InputText type="search" v-model="state.search" placeholder="Search by name or email" fluid />
                </div>
                <ContactSelect
                    :contacts="availableContacts"
                    v-model="selectedMembers"
                    @save="submit"
                    @addContact="state.show.addContactDialog = true"
                />
            </template>
            <template v-else-if="!totalContacts && !state.loading.contacts">
                <div v-if="currentRoute.query.launch" class="mb-8 flex justify-center">
                    <RouterLink :to="{name: 'questions.launch', query: {group: group.id}}">
                        <Button icon="mingcute:send-plane-fill" class="bg-askr-green text-white">
                            Launch questions
                        </Button>
                    </RouterLink>
                </div>
                <p class="text-center">You don't have any contacts, invite some</p>
            </template>
            <PageLoading v-if="state.loading.contacts" class="pt-16" />
            <div v-if="group.invitations?.length" class="mt-12" v-auto-animate>
                <div v-if="state.show.pendingInvitations">
                    <h3 class="mb-2 font-bold">Pending invitations</h3>
                    <div class="flex flex-col gap-1" v-auto-animate>
                        <template v-for="invitation in group.invitations" :key="invitation.id">
                            <Message
                                severity="warn"
                                class="dark:text-white"
                                :pt:text="`flex items-center justify-between w-full text-base leading-none font-medium`"
                            >
                                <div class="flex w-full grow items-center justify-between text-xs">
                                    <span class="font-medium">{{ invitation.first_name }}</span>
                                    <span>&nbsp;</span>
                                    <span class="font-bold">{{ invitation.last_name }}</span>
                                    <Button
                                        :icon="{
                                            icon: 'mdi:refresh',
                                        }"
                                        class="ml-auto !p-0 !px-2 font-semibold"
                                        @click="handleResendInvitation(invitation)"
                                        text
                                    >
                                        Resend
                                    </Button>
                                    <Button
                                        :icon="{
                                            icon: 'mdi:trash-outline',
                                        }"
                                        class="ml-2"
                                        text
                                        @click="handleWithdrawInvitation(invitation)"
                                    />
                                </div>
                            </Message>
                        </template>
                    </div>
                </div>
                <Button
                    @click="state.show.pendingInvitations = !state.show.pendingInvitations"
                    :icon="state.show.pendingInvitations ? 'mdi:chevron-up' : 'mdi:chevron-down'"
                    text
                    class="mt-3 !gap-1 whitespace-nowrap text-xs font-normal text-stone-500"
                >
                    {{ state.show.pendingInvitations ? "Hide" : "Show" }} pending invitations
                </Button>
            </div>
            <div class="mt-6 flex items-center justify-center">
                <Button
                    @click="state.show.addContactDialog = true"
                    icon="mdi:plus"
                    class="border bg-stone-50 !py-1.5 !pl-2 !pr-3 text-xs text-stone-700 shadow-sm dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300"
                >
                    Invite someone else
                </Button>
            </div>
            <AddContactDrawer
                v-model:visible="state.show.addContactDialog"
                :group="group"
                @invited="group.invitations?.push($event)"
            />
        </div>
    </div>
</template>
<style scoped></style>
